import { useCampaignsShow } from '@jobilla/nexus-sdk'
import { useSetAtom } from 'jotai'
import { useEffect } from 'react'
import { campaignDetailsAtom, ConformedCampaignDetails } from '../../ui'
import { useConformImageDataToMediaAsset } from '../helpers'

/**
 * Simplified branding loader hook for post-launch form editing.
 * This only hydrates the campaignDetailsAtom with branding data,
 * which is required for campaignDetailsBrandingAtom to work.
 *
 * Unlike useNewCampaignDetails, this doesn't set up save handlers
 * or logo upload functionality since we only need read access to branding.
 */
export function usePostLaunchBrandingLoader(campaignId: string) {
	const conformImageDataToMediaAsset = useConformImageDataToMediaAsset()
	const setCampaignDetails = useSetAtom(campaignDetailsAtom)

	const campaign = useCampaignsShow(campaignId, {
		staleTime: 1000,
	})

	const campaignData = campaign.data

	useEffect(() => {
		if (!campaignData) {
			return
		}

		const details: ConformedCampaignDetails = {
			title: campaignData.title ?? '',
			location: campaignData.location ?? '',
			language: campaignData.language ?? '',
			requirements: campaignData.requirements ?? '',
			invoiceUrl: campaignData.invoiceUrl ?? '',
			branding: {
				id: campaignData.branding?.id ?? '',
				name: campaignData.branding?.name ?? null,
				logoId: campaignData.branding?.logoId ?? '',
				logo: campaignData.branding?.logo
					? conformImageDataToMediaAsset(campaignData.branding.logo)
					: undefined,
				foreground: campaignData.branding?.foreground ?? '',
				background: campaignData.branding?.background ?? '',
				accentForeground: campaignData.branding?.accentForeground ?? campaignData.branding?.foreground ?? '',
				accentBackground: campaignData.branding?.accentBackground ?? campaignData.branding?.background ?? '',
				toneOfVoice: campaignData.branding?.toneOfVoice ?? '',
				visualGuidance: campaignData.branding?.visualGuidance ?? '',
				website: campaignData.branding?.website ?? '',
				bodyFont: campaignData.branding?.fonts?.body ?? '',
				titleFont: campaignData.branding?.fonts?.title ?? '',
				generateKeyImage: campaignData.branding?.generateKeyImage ?? true,
			},
		}

		setCampaignDetails(details)
	}, [campaignData, conformImageDataToMediaAsset, setCampaignDetails])

	return {
		isPending: campaign.isPending,
	}
}
